// src/pages/HomePage.jsx — Shop World (Dual Layout: Mobile App + Desktop Web)
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getActiveCategories, getAllVendors, getItemsByVendorId,
  getSubcategoriesByCategoryId, getItemsByCategoryId, getAllActiveItems
} from '../firebase/services';
import { useCart } from '../context/CartContext';
import Toast from '../components/Toast';
import './HomePage.css';

export default function HomePage() {
  const navigate = useNavigate();
  const { addToCart, cart, removeFromCart, updateQty: updateCartQty } = useCart();

  const [categories,      setCategories]     = useState([]);
  const [subcategories,   setSubcategories]  = useState([]);
  const [vendors,         setVendors]        = useState([]);
  const [foodVendors,     setFoodVendors]    = useState([]);   // only food-category vendors
  const [allItems,        setAllItems]       = useState([]);
  const [nonFoodItems,    setNonFoodItems]   = useState([]);   // items for non-food categories
  const [foodCategoryIds, setFoodCategoryIds]= useState([]);   // ids of food/restaurant categories
  const [loading,         setLoading]        = useState(true);
  const [currentSlide,    setCurrentSlide]   = useState(0);  // hero desktop
  const [bannerSlide,     setBannerSlide]    = useState(0);  // mobile banner
  const [foodSlide,       setFoodSlide]      = useState(0);  // desktop carousel
  const [selectedCategory,setSelectedCategory] = useState(null);
  const [activeCategory,  setActiveCategory] = useState(null);
  const [activeSubcat,    setActiveSubcat]   = useState('all');
  const [vegFilter,       setVegFilter]      = useState(false);
  const [searchQ,         setSearchQ]        = useState('');
  const [toast,           setToast]          = useState(null);

  const heroSlides = useMemo(() => [
    { tag: 'New Season Arrivals', title: 'Discover', highlight: 'Exquisite', line3: 'Flavours & More',    desc: 'Handpicked — premium ingredients, curated restaurants, lightning-fast delivery.', img: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=900&q=85', discount: '20%' },
    { tag: 'Premium Collection',  title: 'The Art of', highlight: 'Fine Dining', line3: 'Delivered To You', desc: 'Experience the finest kitchens. Order now for delivery within 30 minutes.',         img: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=900&q=85', discount: '15%' },
    { tag: 'Farm to Table',       title: 'Pure &',    highlight: 'Organic',    line3: 'Wellness Meals',   desc: 'Farm-fresh organic produce delivered with care every single day.',                 img: 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=900&q=85', discount: '50%' },
  ], []);

  // ── Load data ──
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [cats, vens] = await Promise.all([getActiveCategories(), getAllVendors()]);
        if (!alive) return;
        setCategories(cats || []);
        setVendors(vens || []);

        // Detect food/restaurant categories by name keywords
        const FOOD_KEYWORDS = ['food', 'restaurant', 'dining', 'eat', 'meal', 'cuisine', 'biryani', 'hotel', 'tiffin', 'snack', 'breakfast', 'lunch', 'dinner'];
        const foodCatIds = (cats || [])
          .filter(c => FOOD_KEYWORDS.some(kw => (c.name || '').toLowerCase().includes(kw)))
          .map(c => c.id);
        setFoodCategoryIds(foodCatIds);

        // Food vendors = vendors belonging to food categories
        const fVendors = (vens || []).filter(v => foodCatIds.includes(v.categoryId));
        setFoodVendors(fVendors);

        if (cats?.length > 0) {
          const subs = await getSubcategoriesByCategoryId(cats[0].id).catch(() => []);
          if (alive) setSubcategories(subs || []);
        }

        // Items for food vendors (displayed in carousels)
        if (fVendors.length > 0) {
          const batches = await Promise.all(fVendors.slice(0, 8).map(v => getItemsByVendorId(v.id).catch(() => [])));
          const merged  = batches.flat().filter(i => i.Active !== false && i.ItemName).slice(0, 20);
          if (alive) setAllItems(merged);
        } else if (vens?.length > 0) {
          // fallback: load from all vendors
          const batches = await Promise.all(vens.slice(0, 8).map(v => getItemsByVendorId(v.id).catch(() => [])));
          const merged  = batches.flat().filter(i => i.Active !== false && i.ItemName).slice(0, 20);
          if (alive) setAllItems(merged);
        }

        // Non-food category items — load items for each non-food category
        const nonFoodCats = (cats || []).filter(c => !foodCatIds.includes(c.id));
        if (nonFoodCats.length > 0) {
          const nfBatches = await Promise.all(nonFoodCats.map(c => getItemsByCategoryId(c.id).catch(() => [])));
          const nfItems = nfBatches.flat().filter(i => i.Active !== false && i.ItemName);
          if (alive) setNonFoodItems(nfItems);
        }
      } catch (e) { console.error(e); }
      finally { if (alive) setLoading(false); }
    }
    load();
    return () => { alive = false; };
  }, []);

  // Load subcategories when category changes
  useEffect(() => {
    if (!activeCategory) { setSubcategories([]); setActiveSubcat('all'); return; }
    getSubcategoriesByCategoryId(activeCategory).then(s => { setSubcategories(s || []); setActiveSubcat('all'); }).catch(() => {});
  }, [activeCategory]);

  // Auto-timers
  useEffect(() => { const t = setInterval(() => setCurrentSlide(p => (p+1) % heroSlides.length), 5500); return () => clearInterval(t); }, [heroSlides.length]);
  useEffect(() => { if (!vendors.length) return; const t = setInterval(() => setBannerSlide(p => (p+1) % Math.min(vendors.length,5)), 3500); return () => clearInterval(t); }, [vendors.length]);
  useEffect(() => { if (!allItems.length) return; const t = setInterval(() => setFoodSlide(p => { const max = Math.max(0, allItems.length-4); return p >= max ? 0 : p+1; }), 2800); return () => clearInterval(t); }, [allItems.length]);

  const getCartQty = useCallback((id) => { const f = cart.find(c => c._key?.startsWith(`${id}__`)); return f ? f.qty : 0; }, [cart]);
  const getVendor  = useCallback((item) => vendors.find(v => v.id === item.VendorId) || null, [vendors]);

  const handleAdd = useCallback((item) => {
    const vendor = getVendor(item);
    addToCart({ id: item.id, name: item.ItemName, price: item.TotalPrice || item.ItemPrice || 0, image: item.ImageBlob || '', type: 'food', vendorId: item.VendorId, vendorName: vendor?.name || '' });
    setToast({ message: `${item.ItemName} added!`, type: 'success' });
  }, [addToCart, getVendor]);

  const handleDec = useCallback((item) => {
    const found = cart.find(c => c._key?.startsWith(`${item.id}__`));
    if (!found) return;
    if (found.qty <= 1) removeFromCart(found._key); else updateCartQty(found._key, found.qty - 1);
  }, [cart, removeFromCart, updateCartQty]);

  const filteredVendors = useMemo(() => {
    // Only food-category vendors in the vendor section
    const base = foodVendors.length > 0 ? foodVendors : vendors.filter(v => foodCategoryIds.includes(v.categoryId));
    return selectedCategory ? base.filter(v => v.categoryId === selectedCategory) : base;
  }, [vendors, foodVendors, foodCategoryIds, selectedCategory]);

  const mobileFilteredVendors = useMemo(() => {
    const base = foodVendors.length > 0 ? foodVendors : vendors;
    return activeCategory ? base.filter(v => v.categoryId === activeCategory) : base;
  }, [vendors, foodVendors, activeCategory]);

  const searchResults = useMemo(() => {
    if (!searchQ.trim()) return { items: [], vendors: [], categories: [] };
    const q = searchQ.toLowerCase();
    const matchItems    = allItems.filter(i =>
      i.ItemName?.toLowerCase().includes(q) || i.ItemDescription?.toLowerCase().includes(q)
    ).slice(0, 15);
    const matchVendors  = vendors.filter(v =>
      v.name?.toLowerCase().includes(q) || v.restaurantType?.toLowerCase().includes(q) || v.area?.city?.toLowerCase().includes(q)
    ).slice(0, 6);
    const matchCats     = categories.filter(cat =>
      cat.name?.toLowerCase().includes(q)
    ).slice(0, 4);
    return { items: matchItems, vendors: matchVendors, categories: matchCats };
  }, [searchQ, allItems, vendors, categories]);
  
  const hasSearchResults = searchQ.trim() && (searchResults.items.length > 0 || searchResults.vendors.length > 0 || searchResults.categories.length > 0);
  const hasNoResults     = searchQ.trim() && !hasSearchResults;

  const bannerVendors = (foodVendors.length > 0 ? foodVendors : vendors).slice(0, 5);

  // ─────────────────────────────────────────────────────────────────
  // SHARED STEPPER component
  const Stepper = ({ item, sm }) => {
    const qty = getCartQty(item.id);
    return qty > 0 ? (
      <div className={`hw-stepper${sm ? ' hw-stepper-sm' : ''}`}>
        <button onClick={() => handleDec(item)}>−</button>
        <span>{qty}</span>
        <button onClick={() => handleAdd(item)}>+</button>
      </div>
    ) : (
      <button className={`hw-add-btn${sm ? ' hw-add-btn-sm' : ''}`} onClick={() => handleAdd(item)}>ADD</button>
    );
  };

  return (
    <>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* ════════════════════════════════════════════════════════
          MOBILE LAYOUT  (≤ 768px)
      ════════════════════════════════════════════════════════ */}
      <div className="hw-mobile-only">
        <div className="hw-app">

          {/* Header */}
          <header className="hw-header">
            <div className="hw-header-top">
              <div className="hw-location">
                <span className="hw-location-label">Home <span className="hw-chevron">▾</span></span>
                <div className="hw-location-addr">Shop World Delivery</div>
              </div>
              <button className="hw-cart-icon-btn" onClick={() => navigate('/cart')}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                {cart.length > 0 && <span className="hw-cart-badge">{cart.reduce((s,i)=>s+i.qty,0)}</span>}
              </button>
            </div>
            <div className="hw-search-wrap">
              <div className="hw-search-box">
                <svg className="hw-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input className="hw-search-input" placeholder="Search restaurants, dishes or category" value={searchQ} onChange={e => setSearchQ(e.target.value)} />
              </div>
              <button className={`hw-veg-btn${vegFilter?' active':''}`} onClick={() => setVegFilter(v=>!v)}>NON-VEG</button>
            </div>
          </header>

          {/* Banner */}
          {!searchQ.trim() && <div className="hw-banner-wrap">
            <div className="hw-banner-track" style={{ transform: `translateX(-${bannerSlide*100}%)` }}>
              {bannerVendors.length > 0 ? bannerVendors.map(v => (
                <div key={v.id} className="hw-banner-slide" onClick={() => navigate(`/vendor/${v.id}`)}>
                  {v.imageBlob ? <img src={v.imageBlob} alt={v.name} className="hw-banner-img" /> : <div className="hw-banner-placeholder"><div className="hw-banner-placeholder-inner"><div className="hw-banner-name">{v.name}</div><div className="hw-banner-phone">Shop World : 8438992853</div><button className="hw-banner-btn">ORDER NOW →</button></div></div>}
                  <div className="hw-banner-gradient" />
                  <div className="hw-banner-info"><div className="hw-banner-vname">{v.name}</div><div className="hw-banner-phone">Shop World : 8438992853</div><button className="hw-banner-btn">ORDER NOW →</button></div>
                </div>
              )) : <div className="hw-banner-slide hw-banner-fallback"><div className="hw-banner-placeholder-inner"><div className="hw-banner-name">Shop World</div><div className="hw-banner-phone">8438992853</div><button className="hw-banner-btn" onClick={()=>navigate('/shop')}>ORDER NOW →</button></div></div>}
            </div>
            {bannerVendors.length > 1 && <div className="hw-banner-dots">{bannerVendors.map((_,i)=><span key={i} className={`hw-banner-dot${i===bannerSlide?' active':''}`} onClick={()=>setBannerSlide(i)}/>)}</div>}
          </div>}

          {/* Main categories */}
          {!searchQ.trim() && <div className="hw-cats-wrap">
            <div className="hw-cats-scroll">
              <button className={`hw-cat-chip${!activeCategory?' active':''}`} onClick={()=>setActiveCategory(null)}><span className="hw-cat-chip-icon">🍽</span><span>All</span></button>
              {categories.map(cat=>(
                <button key={cat.id} className={`hw-cat-chip${activeCategory===cat.id?' active':''}`} onClick={()=>setActiveCategory(cat.id===activeCategory?null:cat.id)}>
                  {cat.imageUrl?<img src={cat.imageUrl} alt={cat.name} className="hw-cat-chip-img"/>:<span className="hw-cat-chip-icon">🍴</span>}
                  <span>{cat.name}</span>
                </button>
              ))}
            </div>
          </div>}

          {/* Subcategories */}
          {!searchQ.trim() && subcategories.length > 0 && (
            <div className="hw-subcat-section">
              <div className="hw-subcat-header">
                <span className="hw-subcat-title">Sub Category</span>
                
              </div>
              <div className="hw-subcat-scroll">
                <div className={`hw-subcat-item${activeSubcat==='all'?' active':''}`} onClick={()=>setActiveSubcat('all')}><div className="hw-subcat-circle"><span style={{fontSize:22}}>🍽</span></div><span className="hw-subcat-label">All</span></div>
                {subcategories.map(sub=>(
                  <div key={sub.id} className={`hw-subcat-item${activeSubcat===sub.id?' active':''}`} onClick={()=>setActiveSubcat(sub.id)}>
                    <div className="hw-subcat-circle">{sub.imageBlob||sub.imageUrl?<img src={sub.imageBlob||sub.imageUrl} alt={sub.name}/>:<span style={{fontSize:20}}>🍴</span>}</div>
                    <span className="hw-subcat-label">{sub.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search results */}
          {searchQ.trim() && (
            <div className="hw-search-results">
              {hasNoResults && (
                <div className="hw-empty">No results found for "{searchQ}"</div>
              )}

              {/* Matching Categories */}
              {searchResults.categories?.length > 0 && (
                <div className="hw-sr-group">
                  <div className="hw-sr-group-title">Categories</div>
                  <div className="hw-sr-cats-row">
                    {searchResults.categories.map(cat => (
                      <button key={cat.id} className="hw-sr-cat-chip"
                        onClick={() => { setActiveCategory(cat.id); setSearchQ(''); }}>
                        {cat.imageUrl ? <img src={cat.imageUrl} alt={cat.name} className="hw-sr-cat-img"/> : <span>🍴</span>}
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Matching Restaurants */}
              {searchResults.vendors?.length > 0 && (
                <div className="hw-sr-group">
                  <div className="hw-sr-group-title">Restaurants</div>
                  {searchResults.vendors.map(v => (
                    <div key={v.id} className="hw-sr-vendor-row" onClick={() => navigate(`/vendor/${v.id}`)}>
                      <div className="hw-sr-vendor-img">
                        {v.imageBlob ? <img src={v.imageBlob} alt={v.name}/> : <div className="hw-sr-vendor-ph">🏪</div>}
                      </div>
                      <div className="hw-sr-vendor-info">
                        <div className="hw-sr-vendor-name">{v.name}</div>
                        <div className="hw-sr-vendor-meta">{v.restaurantType}{v.area?.city ? ` · ${v.area.city}` : ''}</div>
                      </div>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#CCC" strokeWidth="2"><path d="m9 18 6-6-6-6"/></svg>
                    </div>
                  ))}
                </div>
              )}

              {/* Matching Items */}
              {searchResults.items?.length > 0 && (
                <div className="hw-sr-group">
                  <div className="hw-sr-group-title">Dishes</div>
                  <div className="hw-items-list">
                    {searchResults.items.map(item => {
                      const vendor = getVendor(item); const price = item.TotalPrice||item.ItemPrice||0;
                      return (
                        <div key={item.id} className="hw-item-row">
                          <div className="hw-item-row-img" onClick={() => vendor && navigate(`/vendor/${vendor.id}`)}>
                            {item.ImageBlob ? <img src={item.ImageBlob} alt={item.ItemName}/> : <div className="hw-item-row-img-ph">🍽</div>}
                          </div>
                          <div className="hw-item-row-info">
                            <div className="hw-item-row-name">{item.ItemName}</div>
                            {vendor && <div className="hw-item-row-vendor">{vendor.name}</div>}
                            <div className="hw-item-row-price">₹{price}</div>
                          </div>
                          <div className="hw-item-row-ctrl"><Stepper item={item}/></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Popular Restaurants */}
          {!searchQ.trim() && (
            <div className="hw-section">
              <div className="hw-section-header"><span className="hw-section-icon">🍽</span><span className="hw-section-title">Popular Restaurants</span></div>
              {loading?([...Array(3)].map((_,i)=><div key={i} className="skeleton-card" style={{height:200,borderRadius:14,marginBottom:16}}/>)):
              mobileFilteredVendors.length===0?<div className="hw-empty">No restaurants found.</div>:(
                <div className="hw-vendor-list">
                  {mobileFilteredVendors.map(vendor=>{
                    const vitems=allItems.filter(i=>i.VendorId===vendor.id);
                    return(<div key={vendor.id} className="hw-vendor-card" onClick={()=>navigate(`/vendor/${vendor.id}`)}>
                      <div className="hw-vendor-img-wrap">
                        {vendor.imageBlob?<img src={vendor.imageBlob} alt={vendor.name} className="hw-vendor-img"/>:<div className="hw-vendor-img-ph">🏪</div>}
                        <div className="hw-vendor-rating">4.5 ★</div>
                        <button className="hw-vendor-heart" onClick={e=>e.stopPropagation()}>♡</button>
                        {vendor.deliveryCharge===0&&<div className="hw-vendor-free">Free Delivery</div>}
                      </div>
                      <div className="hw-vendor-info">
                        <div className="hw-vendor-name">{vendor.name}</div>
                        <div className="hw-vendor-meta">{vendor.restaurantType&&<span>{vendor.restaurantType}</span>}{vendor.area?.city&&<span>📍 {vendor.area.city}</span>}</div>
                        {vitems.length>0&&<div className="hw-vendor-items-row">
                          {vitems.slice(0,3).map(item=>{const price=item.TotalPrice||item.ItemPrice||0;return(
                            <div key={item.id} className="hw-vendor-item-chip" onClick={e=>e.stopPropagation()}>
                              <div className="hw-vendor-item-name">{item.ItemName}</div>
                              <div className="hw-vendor-item-price">₹{price}</div>
                              <Stepper item={item} sm/>
                            </div>);})}
                        </div>}
                      </div>
                    </div>);
                  })}
                </div>
              )}
            </div>
          )}

          {/* Food Item Selection horizontal scroll */}
          {!searchQ.trim() && allItems.length>0 && (
            <div className="hw-section">
              <div className="hw-section-header"><span className="hw-section-icon">⭐</span><span className="hw-section-title">Item Selection</span></div>
              <div className="hw-items-scroll">
                {allItems.map(item=>{const vendor=getVendor(item);const price=item.TotalPrice||item.ItemPrice||0;return(
                  <div key={item.id} className="hw-item-card">
                    <div className="hw-item-card-img" onClick={()=>vendor&&navigate(`/vendor/${vendor.id}`)}>
                      {item.ImageBlob?<img src={item.ImageBlob} alt={item.ItemName}/>:<div className="hw-item-card-img-ph">🍽</div>}
                      {item.isVeg!==undefined&&<span className={`hw-veg-dot-abs ${item.isVeg?'veg':'nonveg'}`}/>}
                    </div>
                    <div className="hw-item-card-body">
                      <div className="hw-item-card-name">{item.ItemName}</div>
                      {vendor&&<div className="hw-item-card-vendor">{vendor.name}</div>}
                      <div className="hw-item-card-footer"><span className="hw-item-card-price">₹{price}</span><Stepper item={item}/></div>
                    </div>
                  </div>);
                })}
              </div>
            </div>
          )}

          {/* Non-food category items (shoes, clothing, etc.) — shown directly */}
          {!searchQ.trim() && nonFoodItems.length>0 && (
            <div className="hw-section">
              <div className="hw-section-header"><span className="hw-section-icon">🛍</span><span className="hw-section-title">Shop Items</span></div>
              <div className="hw-nf-grid">
                {nonFoodItems.map(item=>{
                  const price=item.TotalPrice||item.ItemPrice||0;
                  return(
                    <div key={item.id} className="hw-nf-card">
                      <div className="hw-nf-img">
                        {item.ImageBlob?<img src={item.ImageBlob} alt={item.ItemName}/>:<div className="hw-nf-img-ph">🛍</div>}
                      </div>
                      <div className="hw-nf-body">
                        <div className="hw-nf-name">{item.ItemName}</div>
                        <div className="hw-nf-footer">
                          <span className="hw-nf-price">₹{price}</span>
                          <Stepper item={item}/>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{height:80}}/>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          DESKTOP LAYOUT  (> 768px)
      ════════════════════════════════════════════════════════ */}
      <div className="hw-desktop-only luxury-wrapper">

        {/* ── HERO ── */}
        <section className="hero-full-section">
          <div className="hero-slider-inner">
            {heroSlides.map((slide,i)=>(
              <div key={i} className={`slide-full${i===currentSlide?' active':''}`}>
                <div className="slide-content-full">
                  <div className="slide-text-side">
                    <span className="slide-tag">{slide.tag}</span>
                    <h1 className="slide-title-full">{slide.title} <span className="text-highlight">{slide.highlight}</span><br/>{slide.line3}</h1>
                    <p className="slide-desc-full">{slide.desc}</p>
                    <div className="slide-actions">
                      <button className="btn btn-primary-slide" onClick={()=>document.getElementById('vendors-section')?.scrollIntoView({behavior:'smooth'})}>Explore Collection</button>
                      <button className="btn-play-slide">▶</button>
                    </div>
                    <div className="slide-features">
                      <div className="slide-feature-pill"><span className="pficon">🚚</span><div><div style={{fontWeight:600,fontSize:12,color:'#fff'}}>Express Delivery</div><div style={{fontSize:10,color:'rgba(255,255,255,0.45)'}}>Within 30 Minutes</div></div></div>
                      <div className="slide-feature-pill"><span className="pficon">⭐</span><div><div style={{fontWeight:600,fontSize:12,color:'#fff'}}>Premium Quality</div><div style={{fontSize:10,color:'rgba(255,255,255,0.45)'}}>Curated & Certified</div></div></div>
                    </div>
                  </div>
                  <div className="slide-image-side">
                    <div className="slide-discount-badge"><span className="badge-pct">{slide.discount}</span><span className="badge-off">Off</span></div>
                    <div className="slide-food-circle"><img src={slide.img} alt={slide.title}/></div>
                    <div className="slide-float-tag"><span style={{fontSize:22}}>🍽</span><div><div style={{fontWeight:700,fontSize:13,color:'#0D1B2A'}}>Order Now</div><div style={{fontSize:10,color:'#8A9BB0'}}>Free Delivery Today</div></div></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="slider-dots-center">{heroSlides.map((_,i)=><button key={i} className={`dot-full${i===currentSlide?' active':''}`} onClick={()=>setCurrentSlide(i)} aria-label={`Slide ${i+1}`}/>)}</div>
        </section>

        {/* ── TRUST BAR ── */}
        <div className="trust-bar">
          <div className="trust-bar-inner">
            {[{icon:'🚚',title:'Express Delivery',sub:'Delivered in 30 minutes'},{icon:'🛡',title:'100% Secure Payment',sub:'Safe & encrypted checkout'},{icon:'🔄',title:'Easy Returns',sub:'Hassle-free refund policy'},{icon:'🎧',title:'24/7 Support',sub:'Always here to help'}].map(item=>(
              <div key={item.title} className="trust-item"><span className="trust-icon">{item.icon}</span><div><div className="trust-title">{item.title}</div><div className="trust-sub">{item.sub}</div></div></div>
            ))}
          </div>
        </div>

        {/* ── ITEM SELECTION CAROUSEL ── */}
        <section className="food-cards-section">
          <div className="container">
            <div className="section-header-luxury">
              <span className="section-tag">Chef's Selection</span>
              <h2 className="section-title">Item Selection</h2>
              <div className="title-underline"/>
            </div>
            {loading?(
              <div className="food-cards-wrapper"><div className="food-cards-track"><div className="food-cards-inner">{[...Array(5)].map((_,i)=><div key={i} className="skeleton-card" style={{flex:'0 0 220px',height:300,borderRadius:18}}/>)}</div></div></div>
            ):allItems.length===0?(
              <div style={{textAlign:'center',padding:'40px 0',color:'var(--text-3)'}}><div style={{fontSize:40,marginBottom:12}}>🍽</div><p>No dishes available right now.</p></div>
            ):(
              <div className="food-cards-wrapper">
                <button className="food-nav-btn" onClick={()=>setFoodSlide(p=>Math.max(0,p-1))} disabled={foodSlide===0}>‹</button>
                <div className="food-cards-track">
                  <div className="food-cards-inner" style={{transform:`translateX(-${foodSlide*246}px)`}}>
                    {allItems.map((item,idx)=>{
                      const vendor=getVendor(item); const qty=getCartQty(item.id); const price=item.TotalPrice||item.ItemPrice||0;
                      const bgs=['#FBF4EA','#EAF4EE','#EAF0FB','#F4EAFB','#FBF4EA'];
                      return(
                        <div key={item.id} className="food-card-item" style={{background:bgs[idx%bgs.length]}}>
                          <div className="food-card-circle" onClick={()=>vendor&&navigate(`/vendor/${vendor.id}`)} style={{cursor:vendor?'pointer':'default'}}>
                            {item.ImageBlob?<img src={item.ImageBlob} alt={item.ItemName} loading="lazy"/>:<div style={{fontSize:32,display:'flex',alignItems:'center',justifyContent:'center',width:'100%',height:'100%'}}>🍽</div>}
                          </div>
                          {vendor&&<div className="food-card-vendor">{vendor.name}</div>}
                          <h4 className="food-card-name">{item.ItemName}</h4>
                          <div className="food-card-price">₹{price}</div>
                          <div className="food-card-bottom">
                            {qty>0?(
                              <div className="food-card-stepper">
                                <button className="food-card-stepper-btn" onClick={()=>handleDec(item)}>−</button>
                                <span className="food-card-stepper-qty">{qty}</span>
                                <button className="food-card-stepper-btn food-card-stepper-inc" onClick={()=>handleAdd(item)}>+</button>
                              </div>
                            ):(
                              <button className="food-card-btn" onClick={()=>handleAdd(item)}>Order Now</button>
                            )}
                            <div className="food-card-heart">♥</div>
                          </div>
                          {item.isVeg!==undefined&&<div className="food-card-rating">{item.isVeg?'🟢 Veg':'🔴 Non-Veg'}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <button className="food-nav-btn" onClick={()=>setFoodSlide(p=>Math.min(Math.max(0,allItems.length-4),p+1))} disabled={foodSlide>=Math.max(0,allItems.length-4)}>›</button>
              </div>
            )}
          </div>
        </section>

        {/* ── CATEGORIES ── */}
        <section className="featured-cat-section">
          <div className="container">
            <div className="section-header-luxury"><span className="section-tag">Collections</span><h2 className="section-title">Our Categories</h2><div className="title-underline"/></div>
            <div className="featured-cat-grid">
              <div className="featured-cat-image-col">
                {(foodVendors[0]||vendors[0])?.imageBlob?<img src={(foodVendors[0]||vendors[0]).imageBlob} alt={(foodVendors[0]||vendors[0]).name}/>:<img src="https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=800&q=85" alt="Featured"/>}
                <div className="featured-cat-overlay">
                  <span className="featured-cat-badge">{(foodVendors[0]||vendors[0])?.name||'Gourmet Burgers'}</span>
                  <span className="featured-cat-new">New Arrival</span>
                  <Link to={(foodVendors[0]||vendors[0])?`/vendor/${(foodVendors[0]||vendors[0]).id}`:'/shop'} className="featured-cat-order-btn">Order Now →</Link>
                </div>
              </div>
              <div className="featured-cat-right">
                <div className="featured-cat-small-grid">
                  {(foodVendors.length>1?foodVendors:vendors).slice(1,5).map(v=>(
                    <Link key={v.id} to={`/vendor/${v.id}`} className="featured-cat-small">
                      {v.imageBlob?<img src={v.imageBlob} alt={v.name} loading="lazy"/>:<div style={{width:'100%',height:'100%',background:'#F5EFE0',display:'flex',alignItems:'center',justifyContent:'center',fontSize:36}}>🏪</div>}
                    </Link>
                  ))}
                  {(foodVendors.length<2&&vendors.length<2)&&['https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=400&q=80','https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&q=80','https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=400&q=80','https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400&q=80'].map((img,i)=>(
                    <div key={i} className="featured-cat-small"><img src={img} alt="Food" loading="lazy"/></div>
                  ))}
                </div>
                <div className="foody-cat-list">
                  {(categories.length>0?categories.slice(0,3):[{id:'1',name:'Food Order',imageUrl:null},{id:'2',name:'Top Restaurants',imageUrl:null},{id:'3',name:'Fast Delivery',imageUrl:null}]).map((cat,i)=>{
                    const bgs=['#FBF4EA','#EAF4EE','#EAF0FB']; const icons=['🍕','🏪','🚚'];
                    return(
                      <Link key={cat.id} to={`/category/${cat.id}`} className="foody-cat-item">
                        <div className="foody-cat-icon-wrap" style={{background:bgs[i%3]}}>{cat.imageUrl?<img src={cat.imageUrl} alt={cat.name}/>:<span>{icons[i%3]}</span>}</div>
                        <div className="foody-cat-text"><div className="foody-cat-name">{cat.name}</div><div className="foody-cat-sub">Fresh &amp; Delivered Daily</div></div>
                        <span className="foody-cat-arrow">›</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── BROWSE VENDORS (Food only) ── */}
        <section className="section-block-luxury" id="vendors-section">
          <div className="container">
            <div className="section-header-luxury"><span className="section-tag">Explore</span><h2 className="section-title">Browse Vendors</h2><div className="title-underline"/></div>
            <div className="home-split-layout">
              <div className="sidebar-left">
                <div className="sidebar-header"><h3>Categories</h3></div>
                {loading?([...Array(4)].map((_,i)=><div key={i} className="skeleton-card" style={{height:44,marginBottom:4}}/>)):(
                  <div className="category-list-vertical">
                    <button className={`cat-list-item${selectedCategory===null?' active':''}`} onClick={()=>setSelectedCategory(null)}><span className="cat-icon">🍕</span><span className="cat-name">All Vendors</span><span className="cat-arrow">→</span></button>
                    {categories.filter(c=>foodCategoryIds.includes(c.id)).map(cat=>(
                      <button key={cat.id} className={`cat-list-item${selectedCategory===cat.id?' active':''}`} onClick={()=>setSelectedCategory(cat.id)}>
                        <span className="cat-icon">{cat.imageUrl?<img src={cat.imageUrl} alt={cat.name}/>:'🍽'}</span>
                        <span className="cat-name">{cat.name}</span>
                        <span className="cat-arrow">→</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="content-right">
                {loading?(<div className="vendor-grid-luxury">{[...Array(6)].map((_,i)=><div key={i} className="skeleton-card" style={{height:300}}/>)}</div>):
                filteredVendors.length===0?(<div className="empty-state"><div style={{fontSize:44,marginBottom:14}}>🏪</div><p>No vendors found.</p></div>):(
                  <div className="vendor-grid-luxury">
                    {filteredVendors.map((vendor,i)=>(
                      <Link key={vendor.id} to={`/vendor/${vendor.id}`} className="vendor-card-luxury anim-fade-up" style={{animationDelay:`${Math.min(i,5)*0.08}s`}}>
                        <div className="vendor-img-box">
                          {vendor.imageBlob?<img src={vendor.imageBlob} alt={vendor.name} loading="lazy"/>:<div className="placeholder-icon">🏪</div>}
                          {vendor.recommended&&<span className="badge-luxury">★ Recommended</span>}
                          <div className="vendor-img-gradient"/>
                          <div className="vendor-hover-overlay"><span>View Menu</span></div>
                        </div>
                        <div className="vendor-content-box">
                          <div className="vendor-top"><span className="vendor-type">{vendor.restaurantType||'Restaurant'}</span><h3>{vendor.name}</h3></div>
                          <div className="vendor-bottom"><span className="vendor-location">📍 {vendor.area?.city||'Near You'}</span></div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── NON-FOOD ITEMS (Shoes, Clothing, etc.) — direct display ── */}
        {nonFoodItems.length > 0 && (
          <section className="section-block-luxury" id="shop-items-section">
            <div className="container">
              <div className="section-header-luxury">
                <span className="section-tag">Shop</span>
                <h2 className="section-title">More Categories</h2>
                <div className="title-underline"/>
              </div>
              {loading ? (
                <div className="nf-desktop-grid">{[...Array(8)].map((_,i)=><div key={i} className="skeleton-card" style={{height:320,borderRadius:16}}/>)}</div>
              ) : (
                <div className="nf-desktop-grid">
                  {nonFoodItems.map((item, idx) => {
                    const price = item.TotalPrice || item.ItemPrice || 0;
                    const qty   = getCartQty(item.id);
                    return (
                      <div key={item.id} className="nf-desktop-card anim-fade-up" style={{animationDelay:`${Math.min(idx,7)*0.06}s`}}>
                        <div className="nf-desktop-img">
                          {item.ImageBlob
                            ? <img src={item.ImageBlob} alt={item.ItemName} loading="lazy"/>
                            : <div className="nf-desktop-img-ph">🛍</div>}
                        </div>
                        <div className="nf-desktop-body">
                          <div className="nf-desktop-name">{item.ItemName}</div>
                          <div className="nf-desktop-price">₹{price}</div>
                          <div className="nf-desktop-ctrl">
                            {qty > 0 ? (
                              <div className="food-card-stepper">
                                <button className="food-card-stepper-btn" onClick={()=>handleDec(item)}>−</button>
                                <span className="food-card-stepper-qty">{qty}</span>
                                <button className="food-card-stepper-btn food-card-stepper-inc" onClick={()=>handleAdd(item)}>+</button>
                              </div>
                            ) : (
                              <button className="nf-desktop-add-btn" onClick={()=>handleAdd(item)}>Add to Cart</button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        )}

      </div>
    </>
  );
}
